import { createVariantAttempt, findPreferredAudioTrack, type VideoVariant } from "./playback-policy.js";
import type { PlayerAudioTrack, PlayerCallbacks, PlayerHandle } from "./player.js";

export type PlayerFactory = (
  video: HTMLVideoElement,
  manifestUrl: string,
  callbacks: PlayerCallbacks,
) => PlayerHandle;

export interface EpisodeManifests {
  main: string;
  fallback: string;
}

export interface PlaybackSessionCallbacks {
  onVariant(variant: VideoVariant): void;
  onAudioTracks(tracks: PlayerAudioTrack[], selectedId: string | null): void;
  onTerminalError(message: string): void;
}

export interface PlaybackSession {
  loadEpisode(manifests: EpisodeManifests): void;
  selectVariant(variant: VideoVariant): void;
  selectAudioTrack(id: string): void;
  destroy(): void;
}

const emptyHandle: PlayerHandle = {
  destroy: () => undefined,
  selectAudioTrack: () => undefined,
};

export function createPlaybackSession(
  video: HTMLVideoElement,
  attach: PlayerFactory,
  callbacks: PlaybackSessionCallbacks,
): PlaybackSession {
  let manifests: EpisodeManifests | null = null;
  let attempt = createVariantAttempt();
  let player = emptyHandle;
  let generation = 0;
  let preferredAudioLocale: string | null = null;
  let audioTracks: PlayerAudioTrack[] = [];

  const attachCurrent = (): void => {
    if (!manifests) return;
    const currentGeneration = ++generation;
    const variant = attempt.current();
    const manifestUrl = manifests[variant];
    player.destroy();
    player = emptyHandle;
    audioTracks = [];
    callbacks.onVariant(variant);
    callbacks.onAudioTracks([], null);
    let attached: PlayerHandle | null = null;
    let pendingAudioId: string | null = null;

    const handle = attach(video, manifestUrl, {
      onFatalError: () => {
        if (currentGeneration !== generation) return;
        const alternate = attempt.alternateAfterFailure();
        if (alternate) {
          attachCurrent();
          return;
        }
        generation += 1;
        player.destroy();
        player = emptyHandle;
        callbacks.onTerminalError("Không thể phát cả hai luồng video.");
      },
      onAudioTracks: tracks => {
        if (currentGeneration !== generation) return;
        audioTracks = tracks;
        const preferred = findPreferredAudioTrack(tracks, preferredAudioLocale);
        const selectedId = preferred?.id ?? tracks.find(track => track.selected)?.id ?? null;
        callbacks.onAudioTracks(tracks, selectedId);
        if (preferred && !preferred.selected) {
          if (attached) attached.selectAudioTrack(preferred.id);
          else pendingAudioId = preferred.id;
        }
      },
    });
    attached = handle;
    if (currentGeneration !== generation) {
      handle.destroy();
      return;
    }
    player = handle;
    if (pendingAudioId) player.selectAudioTrack(pendingAudioId);
  };

  return {
    loadEpisode: nextManifests => {
      manifests = nextManifests;
      attempt = createVariantAttempt();
      attachCurrent();
    },
    selectVariant: variant => {
      if (!manifests) return;
      attempt.select(variant);
      attachCurrent();
    },
    selectAudioTrack: id => {
      const selected = audioTracks.find(track => track.id === id);
      if (!selected) return;
      preferredAudioLocale = selected.locale;
      player.selectAudioTrack(id);
    },
    destroy: () => {
      generation += 1;
      player.destroy();
      player = emptyHandle;
      audioTracks = [];
    },
  };
}
